/**
 * useOnboardingSettingsParam — URL state of the "Onboarding settings" drawer.
 *
 * The drawer is open while `?settings` is present on `/project/:id/onboardings-v2`; a known value
 * (`?settings=locales`, `?settings=fonts`) also expands that section (sidebar plan 2026-10-07, D-32).
 */

import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export const ONBOARDING_SETTINGS_QUERY_PARAM = 'settings';

export type OnboardingSettingsSection = 'fonts' | 'locales';

export const ONBOARDING_SETTINGS_SECTIONS: readonly OnboardingSettingsSection[] = ['fonts', 'locales'];

export function parseOnboardingSettingsSection(value: string | null | undefined): OnboardingSettingsSection | null {
  return ONBOARDING_SETTINGS_SECTIONS.includes(value as OnboardingSettingsSection)
    ? (value as OnboardingSettingsSection)
    : null;
}

/** Deep link to the drawer, e.g. from the onboarding-creation language prompt. */
export function buildOnboardingSettingsPath(projectId: string, section?: OnboardingSettingsSection): string {
  return `/project/${projectId}/onboardings-v2?${ONBOARDING_SETTINGS_QUERY_PARAM}=${section ?? ''}`;
}

export interface UseOnboardingSettingsParamResult {
  open: boolean;
  section: OnboardingSettingsSection | null;
  openSettings: (section?: OnboardingSettingsSection) => void;
  closeSettings: () => void;
}

export function useOnboardingSettingsParam(): UseOnboardingSettingsParamResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(ONBOARDING_SETTINGS_QUERY_PARAM);

  const openSettings = useCallback((section?: OnboardingSettingsSection) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(ONBOARDING_SETTINGS_QUERY_PARAM, section ?? '');
      return next;
    });
  }, [setSearchParams]);

  const closeSettings = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(ONBOARDING_SETTINGS_QUERY_PARAM);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return {
    open: raw !== null,
    section: parseOnboardingSettingsSection(raw),
    openSettings,
    closeSettings,
  };
}
